import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FormationHorizontalAlignment,
  FormationVerticalAlignment,
} from '../enum/EnumRepository';
import {
  applyFormationFitDragDelta,
  getFormationContainImageLayout,
  getFormationFitAlignmentAxis,
  getFormationHorizontalAlignmentFromPosition,
  getFormationHorizontalAlignmentPosition,
  getFormationVerticalAlignmentFromPosition,
  getFormationVerticalAlignmentPosition,
  isPointInsideFormationContainImage,
  snapFormationFitPosition,
} from '../util/formationUtils';
import { applyRubberBandFocus, clampFocus } from '../util/coverImageFocus';

const DRAG_THRESHOLD_PX = 3;
const SNAP_DURATION_MS = 100;

type FitPanAxis = 'x' | 'y';

interface UseFitImageSnapPanOptions {
  imageWidth: number;
  imageHeight: number;
  horizontalAlignment: FormationHorizontalAlignment;
  verticalAlignment: FormationVerticalAlignment;
  isEnabled: boolean;
  onAlignmentChange: (
    horizontalAlignment: FormationHorizontalAlignment,
    verticalAlignment: FormationVerticalAlignment,
  ) => void;
}

interface FitPanContext {
  axis: FitPanAxis;
  whitespace: number;
  fixedPosition: number;
}

function applyPositionToElement(
  element: HTMLElement,
  axis: FitPanAxis,
  position: number,
  fixedPosition: number,
) {
  const positionX = axis === 'x' ? position : fixedPosition;
  const positionY = axis === 'y' ? position : fixedPosition;
  element.style.backgroundPosition = `${positionX * 100}% ${positionY * 100}%`;
}

function animatePositionSnap(
  element: HTMLElement,
  context: FitPanContext,
  from: number,
  to: number,
  animationToken: { current: number },
  onComplete: () => void,
) {
  const token = animationToken.current + 1;
  animationToken.current = token;
  const startTime = performance.now();

  const tick = (now: number) => {
    if (animationToken.current !== token) return;

    const progress = Math.min(1, (now - startTime) / SNAP_DURATION_MS);
    const eased = 1 - (1 - progress) ** 3;
    const position = from + (to - from) * eased;
    applyPositionToElement(
      element,
      context.axis,
      position,
      context.fixedPosition,
    );

    if (progress < 1) {
      requestAnimationFrame(tick);
      return;
    }

    onComplete();
  };

  requestAnimationFrame(tick);
}

export function useFitImageSnapPan({
  imageWidth,
  imageHeight,
  horizontalAlignment,
  verticalAlignment,
  isEnabled,
  onAlignmentChange,
}: UseFitImageSnapPanOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const isPointerActiveRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, position: 0 });
  const dragPositionRef = useRef(0);
  const dragVisualPositionRef = useRef(0);
  const dragContextRef = useRef<FitPanContext | null>(null);
  const snapAnimationTokenRef = useRef(0);
  const activeElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isEnabled) return;

    snapAnimationTokenRef.current += 1;
    activeElementRef.current?.style.removeProperty('background-position');
    activeElementRef.current = null;
    isPointerActiveRef.current = false;
    hasDraggedRef.current = false;
    dragContextRef.current = null;
    setIsDragging(false);
  }, [isEnabled]);

  useEffect(() => () => {
    snapAnimationTokenRef.current += 1;
    activeElementRef.current?.style.removeProperty('background-position');
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isEnabled || event.button !== 0) return;
      if ((event.target as HTMLElement).closest('button')) return;

      const containerWidth = event.currentTarget.clientWidth;
      const containerHeight = event.currentTarget.clientHeight;
      const layout = getFormationContainImageLayout(
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
      );
      const alignmentAxis = getFormationFitAlignmentAxis(
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
      );
      if (!layout || alignmentAxis === 'none') return;

      const positionX = getFormationHorizontalAlignmentPosition(horizontalAlignment);
      const positionY = getFormationVerticalAlignmentPosition(verticalAlignment);
      const elementRect = event.currentTarget.getBoundingClientRect();
      if (!isPointInsideFormationContainImage(
        event.clientX - elementRect.left,
        event.clientY - elementRect.top,
        layout,
        positionX,
        positionY,
      )) {
        return;
      }

      const axis: FitPanAxis = alignmentAxis === 'horizontal' ? 'x' : 'y';
      const position = axis === 'x' ? positionX : positionY;
      const fixedPosition = axis === 'x' ? positionY : positionX;
      const whitespace = axis === 'x' ? layout.whitespaceX : layout.whitespaceY;
      if (whitespace <= 0) return;

      snapAnimationTokenRef.current += 1;
      event.currentTarget.style.backgroundPosition = '';
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      activeElementRef.current = event.currentTarget;
      isPointerActiveRef.current = true;
      hasDraggedRef.current = false;
      dragContextRef.current = { axis, whitespace, fixedPosition };
      dragStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        position,
      };
      dragPositionRef.current = position;
      dragVisualPositionRef.current = position;
    }, [
      horizontalAlignment,
      imageHeight,
      imageWidth,
      isEnabled,
      verticalAlignment,
    ],
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPointerActiveRef.current) return;

    const context = dragContextRef.current;
    if (!context) return;

    const delta = context.axis === 'x'
      ? event.clientX - dragStartRef.current.pointerX
      : event.clientY - dragStartRef.current.pointerY;
    if (!hasDraggedRef.current && Math.abs(delta) < DRAG_THRESHOLD_PX) return;

    if (!hasDraggedRef.current) {
      hasDraggedRef.current = true;
      setIsDragging(true);
    }

    const rawPosition = applyFormationFitDragDelta(
      dragStartRef.current.position,
      delta,
      context.whitespace,
    );
    const clampedPosition = clampFocus(rawPosition);
    const visualPosition = applyRubberBandFocus(rawPosition);

    dragPositionRef.current = clampedPosition;
    dragVisualPositionRef.current = visualPosition;
    applyPositionToElement(
      event.currentTarget,
      context.axis,
      visualPosition,
      context.fixedPosition,
    );
  }, []);

  const finishDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, shouldCommit: boolean) => {
      if (!isPointerActiveRef.current) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const context = dragContextRef.current;
      const hasDragged = hasDraggedRef.current;
      const element = event.currentTarget;

      isPointerActiveRef.current = false;
      hasDraggedRef.current = false;
      dragContextRef.current = null;
      setIsDragging(false);

      if (!context || !hasDragged || !shouldCommit) {
        element.style.backgroundPosition = '';
        activeElementRef.current = null;
        return;
      }

      const snappedPosition = snapFormationFitPosition(dragPositionRef.current);
      if (context.axis === 'x') {
        const nextAlignment = getFormationHorizontalAlignmentFromPosition(
          snappedPosition,
        );
        if (nextAlignment !== horizontalAlignment) {
          onAlignmentChange(nextAlignment, verticalAlignment);
        }
      } else {
        const nextAlignment = getFormationVerticalAlignmentFromPosition(
          snappedPosition,
        );
        if (nextAlignment !== verticalAlignment) {
          onAlignmentChange(horizontalAlignment, nextAlignment);
        }
      }

      animatePositionSnap(
        element,
        context,
        dragVisualPositionRef.current,
        snappedPosition,
        snapAnimationTokenRef,
        () => {
          element.style.backgroundPosition = '';
          activeElementRef.current = null;
        },
      );
    }, [
      horizontalAlignment,
      onAlignmentChange,
      verticalAlignment,
    ],
  );

  const panHandlers = useMemo(() => (
    isEnabled
      ? {
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
            finishDrag(event, true);
          },
          onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => {
            finishDrag(event, false);
          },
        }
      : {}
  ), [handlePointerDown, handlePointerMove, finishDrag, isEnabled]);

  const canPan = isEnabled && imageWidth > 0 && imageHeight > 0;

  return {
    canPan,
    isDragging,
    panHandlers: canPan ? panHandlers : {},
  };
}
